/**
 * PWA Install Button Component
 * 
 * Shows an install button when the app is installable
 */

import React, { useState, useEffect } from 'react';
import { showInstallPrompt, isInstallable, isLikelyInstalled } from '../serviceWorkerRegistration';
import './InstallButton.css';

const InstallButton = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalledApp, setIsInstalledApp] = useState(false);
  const [showManualInstallHelp, setShowManualInstallHelp] = useState(false);

  useEffect(() => {
    const syncInstallState = () => {
      setIsInstalledApp(isLikelyInstalled());
      setCanInstall(isInstallable());
    };

    syncInstallState();

    window.addEventListener('pwa:installable', syncInstallState);
    window.addEventListener('pwa:installable-changed', syncInstallState);
    window.addEventListener('pwa:installed', syncInstallState);

    return () => {
      window.removeEventListener('pwa:installable', syncInstallState);
      window.removeEventListener('pwa:installable-changed', syncInstallState);
      window.removeEventListener('pwa:installed', syncInstallState);
    };
  }, []);

  const handleInstall = async () => {
    const result = await showInstallPrompt();

    if (!result || result.status === 'not-available' || result.status === 'error' || result.status === 'dismissed') {
      setShowManualInstallHelp(true);
    }
  };

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/crios|fxios|edgios|opr\//.test(userAgent);
  const isIOSSafari = isIOS && isSafari;

  const manualInstallMessage = isIOSSafari
    ? 'On iPhone/iPad: tap Share and then Add to Home Screen.'
    : 'This browser session did not provide an install prompt. Open browser menu and choose Install app or Add to Home screen.';

  // Don't show install UI if already installed
  if (isInstalledApp) {
    return null;
  }

  if (!canInstall) {
    return (
      <div className="install-fallback" aria-live="polite">
        <button
          type="button"
          className="install-button install-button--manual"
          onClick={() => setShowManualInstallHelp((prev) => !prev)}
        >
          Install App
        </button>
        <p className="install-help-text">
          {showManualInstallHelp ? manualInstallMessage : 'How to install this app'}
        </p>
      </div>
    );
  }

  return (
    <button className="install-button" onClick={handleInstall}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      Install App
    </button>
  );
};

export default InstallButton;
